import { useEffect, useMemo, useState } from 'react';
import { cn } from '../../lib/cn';

const frameWidth = 192;
const frameHeight = 208;
const sheetColumns = 8;
const sheetRows = 9;

const presets = {
  idle: { row: 0, columns: [0, 1, 2, 3, 4, 5], intervalMs: 260 },
  loading: { row: 1, columns: [0, 1, 2, 3, 4, 5, 6, 7], intervalMs: 130 },
  wave: { row: 3, columns: [0, 1, 2, 3], intervalMs: 220 },
  guide: { row: 8, columns: [0, 1, 2, 3, 4, 5], intervalMs: 220 },
};

function useReducedMotion() {
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const query = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    if (!query) {
      return undefined;
    }

    setReducedMotion(query.matches);

    const listener = (event) => setReducedMotion(event.matches);
    query.addEventListener('change', listener);

    return () => query.removeEventListener('change', listener);
  }, []);

  return reducedMotion;
}

export function BearySprite({
  preset = 'idle',
  size = 88,
  label = 'Beary family guide',
  decorative = false,
  className,
}) {
  const reducedMotion = useReducedMotion();
  const config = presets[preset] || presets.idle;
  const [frameIndex, setFrameIndex] = useState(0);
  const scale = size / frameHeight;
  const width = frameWidth * scale;
  const column = config.columns[reducedMotion ? 0 : frameIndex] || 0;

  useEffect(() => {
    setFrameIndex(0);

    if (reducedMotion || config.columns.length <= 1) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setFrameIndex((current) => (current + 1) % config.columns.length);
    }, config.intervalMs);

    return () => window.clearInterval(timer);
  }, [config, reducedMotion]);

  const style = useMemo(
    () => ({
      width,
      height: size,
      backgroundImage: 'url("/images/beary-spritesheet.webp")',
      backgroundRepeat: 'no-repeat',
      backgroundSize: `${frameWidth * sheetColumns * scale}px ${frameHeight * sheetRows * scale}px`,
      backgroundPosition: `${-column * frameWidth * scale}px ${-config.row * frameHeight * scale}px`,
    }),
    [column, config.row, scale, size, width]
  );

  return (
    <span
      className={cn('inline-block shrink-0 overflow-hidden', className)}
      style={style}
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative ? 'true' : undefined}
      aria-label={decorative ? undefined : label}
    />
  );
}

export function BearyGuide({
  message,
  preset = 'guide',
  size = 84,
  className,
  centered = false,
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/60 px-4 py-3 text-sm font-semibold leading-6 text-app-muted',
        centered && 'flex-col text-center',
        className
      )}
    >
      <BearySprite preset={preset} size={size} label={preset === 'loading' ? 'Beary is helping load this' : 'Beary family guide'} />
      <span>{message}</span>
    </div>
  );
}
